<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'FieldsetContractClientData',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    contract: {
      type: Object,
      required: true,
    },
    labelAlign: {
      type: String,
      required: false,
      default: 'right',
    },
    noLegend: {
      type: Boolean,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const ux = ref([]);
  const b_1040167445267876_dataSource = computed(() => ({
    name: 'suggest_company',
    parameters: {
      exclude: props.contract.company ? props.contract.company.id : null,
    },
  }));

  const $data = computed(() => ({
    ux: ux.value,
    b_1040167445267876_dataSource: b_1040167445267876_dataSource.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'ux' },
      { name: 'b_1040167445267876_dataSource', nodeId: '1040167445267876' },
    ],
    loaders: {},
    ux: {},
    dataRefs: { ux, b_1040167445267876_dataSource },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.contract,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('contract');
    }
  );
  watch(
    () => props.labelAlign,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('labelAlign');
    }
  );
  watch(
    () => props.noLegend,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('noLegend');
    }
  );

  const act_1040167445267876_setData_ybd55qami_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'contract.counterparty',
      value: $event.data.response.data,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_1040167445267876_request_yw5rsinzs_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'read_company',
      parameters: {
        id: $event.data.value.id,
      },
      headers: {
        accept: 'application/vnd.itp.contractparty+json',
      },
    };
  };

  const act_7315092382398562_reloadComponentData_7ekrozmfi =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_j1izwuj4s =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_7ekrozmfi,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_j1izwuj4s],
    event: 'reload',
  });
  const act_1040167445267876_setData_ybd55qami = new actionTypes.SetDataAction({
    actionArgs: act_1040167445267876_setData_ybd55qami_getActionArgs,
  });
  const inst_evh_1040167445267876_success_yx0de6rq2 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_setData_ybd55qami,
      event: 'success',
      displayName: 'setData',
    });
  const act_1040167445267876_request_yw5rsinzs = new actionTypes.RequestAction({
    actionArgs: act_1040167445267876_request_yw5rsinzs_getActionArgs,
    events: [inst_evh_1040167445267876_success_yx0de6rq2],
  });
  const inst_evh_1040167445267876_optionSelected_xlr80a32j =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_request_yw5rsinzs,
      event: 'optionSelected',
      displayName: 'request',
    });
  const inst_evh_1040167445267876_optionSelected =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_1040167445267876_optionSelected_xlr80a32j],
      event: 'optionSelected',
    });

  const actions = {
    inst_evh_7315092382398562_reload_j1izwuj4s,
    inst_evh_7315092382398562_reload,
    inst_evh_1040167445267876_success_yx0de6rq2,
    inst_evh_1040167445267876_optionSelected_xlr80a32j,
    inst_evh_1040167445267876_optionSelected,
  };

  const evh_7315092382398562_reload_j1izwuj4s = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_j1izwuj4s.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_yx0de6rq2 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_yx0de6rq2.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_optionSelected_xlr80a32j = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_1040167445267876_optionSelected_xlr80a32j.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_optionSelected = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_optionSelected.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <legend v-if="!!noLegend">
      {{ pgettext('Contract', 'Tenant') }}
    </legend>
    <ItpFormGroup
      name="client"
      :label="pgettext('Contract', 'Client')"
      :label-cols-md="4"
      :content-cols-md="8"
      :labelAlign="labelAlign"
      required
    >
      <ItpSelect2
        name="client"
        v-model="contract.counterparty"
        :value="contract.counterparty"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (contract.counterparty =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
        bind-object
        :dataSource="b_1040167445267876_dataSource"
        textField="name"
        required
        optionTemplate="<UcSuggestCompanyOption :option=option></UcSuggestCompanyOption>"
        use-option-template-for-selected-option
        @optionSelected="evh_1040167445267876_optionSelected($event, {})"
      >
      </ItpSelect2>
    </ItpFormGroup>
    <ItpFormGroup
      name="head_office"
      :label="pgettext('Company', 'Head office')"
      :label-cols-md="4"
      :content-cols-md="8"
      :labelAlign="labelAlign"
      v-if="contract.counterparty"
    >
      <ItpFormInput
        name="head_office"
        v-model="contract.counterparty.head_office"
        :value="contract.counterparty.head_office"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (contract.counterparty.head_office =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
      >
      </ItpFormInput>
    </ItpFormGroup>
    <ItpFormGroup
      name="branch_office"
      :label="pgettext('Company', 'Branch office')"
      :label-cols-md="4"
      :content-cols-md="8"
      :labelAlign="labelAlign"
      v-if="contract.counterparty"
    >
      <ItpFormInput
        name="branch_office"
        v-model="contract.counterparty.branch_office"
        :value="contract.counterparty.branch_office"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (contract.counterparty.branch_office =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
      >
      </ItpFormInput>
    </ItpFormGroup>
    <ItpFormGroup
      name="statistical_code"
      :label="pgettext('Company', 'Statistical code')"
      :label-cols-md="4"
      :content-cols-md="4"
      :labelAlign="labelAlign"
      v-if="contract.counterparty"
    >
      <ItpFormInput
        name="statistical_code"
        v-model="contract.counterparty.statistical_code"
        :value="contract.counterparty.statistical_code"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (contract.counterparty.statistical_code =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
      >
      </ItpFormInput>
    </ItpFormGroup>
    <ItpFormGroup
      name="registration_number"
      :label="pgettext('Company', 'Registration number')"
      :label-cols-md="4"
      :content-cols-md="4"
      :labelAlign="labelAlign"
      v-if="contract.counterparty"
    >
      <ItpFormInput
        name="registration_number"
        v-model="contract.counterparty.registration_number"
        :value="contract.counterparty.registration_number"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (contract.counterparty.registration_number =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
      >
      </ItpFormInput>
    </ItpFormGroup>
    <ItpFormGroup
      name="account_number"
      :label="pgettext('Company', 'Account number')"
      :label-cols-md="4"
      :content-cols-md="8"
      :labelAlign="labelAlign"
      v-if="contract.counterparty"
    >
      <ItpFormInput
        name="account_number"
        v-model="contract.counterparty.account_number"
        :value="contract.counterparty.account_number"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (contract.counterparty.account_number =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
      >
      </ItpFormInput>
    </ItpFormGroup>
    <ItpFormGroup
      name="representative"
      :label="pgettext('Company', 'Representative')"
      :label-cols-md="4"
      :content-cols-md="8"
      :labelAlign="labelAlign"
      v-if="contract.counterparty"
    >
      <ItpFormTextarea
        name="representative"
        v-model="contract.counterparty.representative"
        :value="contract.counterparty.representative"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (contract.counterparty.representative =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
      >
      </ItpFormTextarea>
    </ItpFormGroup>
  </ItpUsercontrol>
</template>
